import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_LINES = [
  { text: "Find", italic: true, extra: " clarity." },
  { text: "Build connection." },
  { text: "Heal", italic: true, extra: " together." },
];

const ease = [0.16, 1, 0.3, 1];

export default function Hero({ start }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yBadge = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full pt-32 sm:pt-36 pb-16"
    >
      <div className="mx-auto grid max-w-[1440px] grid-cols-12 gap-6 px-6 sm:px-10">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.05, ease }}
          className="col-span-12 flex items-center justify-between"
        >
          <div className="flex items-center gap-3 text-[11px] font-sans-ui uppercase tracking-[0.3em] text-[#5C5C59]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2C4C3B]" />
            California Telehealth · Licensed MFT #122546
          </div>
          <div className="hidden sm:block text-[11px] font-sans-ui uppercase tracking-[0.3em] text-[#5C5C59]">
            (No. 01) — Sessions accepting
          </div>
        </motion.div>

        {/* Headline */}
        <div className="col-span-12 lg:col-span-8 mt-14 sm:mt-20">
          <h1
            data-testid="hero-headline"
            className="font-serif-display font-light tracking-tighter leading-[0.95] text-[16vw] sm:text-[13vw] lg:text-[10.5vw] text-[#1A1A18]"
          >
            {HERO_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={start ? { y: "0%" } : {}}
                  transition={{ duration: 1.1, delay: 0.15 + i * 0.16, ease }}
                  className="block will-change-transform"
                >
                  {line.italic ? <em className="italic">{line.text}</em> : line.text}
                  {line.extra}
                </motion.span>
              </span>
            ))}
          </h1>

          {/* Sub + CTA */}
          <div className="mt-12 grid grid-cols-12 gap-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={start ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.85, ease }}
              className="col-span-12 md:col-span-6 lg:col-span-5 font-sans-ui text-base sm:text-lg leading-relaxed text-[#5C5C59]"
              data-testid="hero-subhead"
            >
              A private telehealth practice for couples, families, and individuals across
              California. Warm, considered, evidence-informed therapy — for the seasons of life
              that ask more of us.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={start ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 1.0, ease }}
              className="col-span-12 md:col-span-6 lg:col-span-4 lg:col-start-9 flex flex-col items-start gap-4 md:items-end md:text-right"
            >
              <a
                href="#book"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-[#2C4C3B] px-7 py-4 text-[12px] font-medium uppercase tracking-[0.24em] text-[#F7F5F0] hover:bg-[#1E382A] active:scale-[0.98] transition-[background-color,transform] duration-200"
              >
                Request a free consultation
                <span className="inline-block h-6 w-6 border border-[#F7F5F0]/40 rounded-full grid place-items-center transition-transform group-hover:translate-x-1">
                  →
                </span>
              </a>
              <a
                data-testid="hero-phone"
                href="tel:2096706799"
                className="link-under font-sans-ui text-[13px] uppercase tracking-[0.22em] text-[#1A1A18]"
              >
                Or call — 209.670.6799
              </a>
            </motion.div>
          </div>
        </div>

        {/* Image */}
        <div className="col-span-12 lg:col-span-4 mt-16 lg:mt-24">
          <div className="relative aspect-[3/4] w-full overflow-hidden border border-[#D5D1C9]">
            <motion.img
              src="https://images.pexels.com/photos/7156320/pexels-photo-7156320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
              alt="A Black couple sharing a warm embrace in soft window light"
              style={{ y: yImg, scale: scaleImg }}
              className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
              draggable="false"
            />
            <motion.div
              style={{ y: yBadge }}
              className="absolute left-4 top-4 bg-[#F7F5F0] px-3 py-1.5 font-sans-ui text-[10px] uppercase tracking-[0.28em] text-[#1A1A18] border border-[#D5D1C9]"
            >
              Fig. 01 — Presence
            </motion.div>
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-4 bg-gradient-to-t from-[#1A1A18]/50 to-transparent">
              <span className="font-serif-display italic text-[#F7F5F0] text-lg">
                held, warmly
              </span>
              <span className="font-sans-ui text-[10px] uppercase tracking-[0.28em] text-[#F7F5F0]/80">
                CA · 2025
              </span>
            </div>
          </div>

          {/* Small caption */}
          <div className="mt-4 flex items-start justify-between gap-4">
            <p className="font-sans-ui text-[12px] text-[#5C5C59] max-w-[240px] leading-relaxed">
              Every session begins with a slow inhale. What you bring, we hold — carefully.
            </p>
            <span className="font-serif-display italic text-[#1A1A18] text-lg">— dlg</span>
          </div>
        </div>
      </div>

      {/* Bottom rule */}
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 mt-16">
        <div className="rule" />
      </div>
    </section>
  );
}
